import api from './authService';
import type {
  Tenant,
  TenantCreate,
  TenantUpdate,
  TenantListResponse,
  TenantStats,
  TenantBranding,
  TenantBrandingPublic,
  TenantProduct,
  TenantProductCreate,
  TenantProductUpdate,
  TenantProductListResponse
} from '@types';

// ===========================================
// スーパーアドミン用API
// ===========================================

// テナント一覧取得
export const getTenants = async (
  skip = 0,
  limit = 50,
  search?: string,
  isActive?: number
): Promise<TenantListResponse> => {
  const params = new URLSearchParams();
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());
  if (search) params.append('search', search);
  if (isActive !== undefined) params.append('is_active', isActive.toString());

  const response = await api.get<TenantListResponse>(`/admin/tenants?${params.toString()}`);
  return response.data;
};

// テナント詳細取得
export const getTenant = async (id: number): Promise<Tenant> => {
  const response = await api.get<Tenant>(`/admin/tenants/${id}`);
  return response.data;
};

// テナント作成
export const createTenant = async (data: TenantCreate): Promise<Tenant> => {
  const response = await api.post<Tenant>('/admin/tenants', data);
  return response.data;
};

// テナント更新
export const updateTenant = async (id: number, data: TenantUpdate): Promise<Tenant> => {
  const response = await api.put<Tenant>(`/admin/tenants/${id}`, data);
  return response.data;
};

// テナント削除
export const deleteTenant = async (id: number): Promise<void> => {
  await api.delete(`/admin/tenants/${id}`);
};

// テナント一時停止
export const suspendTenant = async (id: number, reason: string): Promise<Tenant> => {
  const response = await api.post<Tenant>(`/admin/tenants/${id}/suspend?reason=${encodeURIComponent(reason)}`);
  return response.data;
};

// テナント再開
export const activateTenant = async (id: number): Promise<Tenant> => {
  const response = await api.post<Tenant>(`/admin/tenants/${id}/activate`);
  return response.data;
};

// APIキー再生成
export const regenerateApiKey = async (id: number): Promise<{ api_key: string; webhook_secret: string }> => {
  const response = await api.post<{ api_key: string; webhook_secret: string }>(`/admin/tenants/${id}/regenerate-api-key`);
  return response.data;
};

// テナント統計取得
export const getTenantStats = async (id: number): Promise<TenantStats> => {
  const response = await api.get<TenantStats>(`/admin/tenants/${id}/stats`);
  return response.data;
};

// ===========================================
// テナント管理者用API
// ===========================================

// 自分のテナント情報取得
export const getMyTenantInfo = async (): Promise<Tenant> => {
  const response = await api.get<Tenant>('/my-tenant/info');
  return response.data;
};

// 自分のテナントブランディング取得
export const getMyTenantBranding = async (): Promise<TenantBrandingPublic> => {
  const response = await api.get<TenantBrandingPublic>('/my-tenant/branding');
  return response.data;
};

// 自分のテナントブランディング更新
export const updateMyTenantBranding = async (data: TenantBranding): Promise<TenantBrandingPublic> => {
  const response = await api.put<TenantBrandingPublic>('/my-tenant/branding', data);
  return response.data;
};

// 自分のテナント統計取得
export const getMyTenantStats = async (): Promise<TenantStats> => {
  const response = await api.get<TenantStats>('/my-tenant/stats');
  return response.data;
};

// ===========================================
// 公開API（認証不要）
// ===========================================

// テナントブランディング取得（認証不要）
export const getPublicBranding = async (tenantCode?: string): Promise<TenantBrandingPublic> => {
  const url = tenantCode ? `/tenant/branding/${tenantCode}` : '/tenant/branding';
  const response = await api.get<TenantBrandingPublic>(url);
  return response.data;
};

// テナント識別（デバッグ用）
export const identifyTenant = async (): Promise<{
  identified: boolean;
  tenant_code: string | null;
  tenant_name: string | null;
  tenant_id: number | null;
  message?: string;
}> => {
  const response = await api.get('/tenant/identify');
  return response.data;
};

// ===========================================
// テナント×プロダクト管理（スーパーアドミン）
// ===========================================

// テナントのプロダクト設定一覧取得
export const getTenantProducts = async (tenantId: number): Promise<TenantProductListResponse> => {
  const response = await api.get<TenantProductListResponse>(`/admin/tenants/${tenantId}/products`);
  return response.data;
};

// テナントにプロダクトを追加
export const createTenantProduct = async (tenantId: number, data: TenantProductCreate): Promise<TenantProduct> => {
  const response = await api.post<TenantProduct>(`/admin/tenants/${tenantId}/products`, data);
  return response.data;
};

// テナントのプロダクト設定を更新
export const updateTenantProduct = async (
  tenantId: number,
  productId: number,
  data: TenantProductUpdate
): Promise<TenantProduct> => {
  const response = await api.put<TenantProduct>(`/admin/tenants/${tenantId}/products/${productId}`, data);
  return response.data;
};

// テナントからプロダクトを削除
export const deleteTenantProduct = async (tenantId: number, productId: number): Promise<void> => {
  await api.delete(`/admin/tenants/${tenantId}/products/${productId}`);
};
